import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../security/security/AuthContext';
import { checkUserStatus } from '../../security/api/ApiClient';
import { FaBitcoin, FaEthereum, FaLock, FaEnvelope, FaArrowRight } from 'react-icons/fa';
import { SiRipple, SiLitecoin } from 'react-icons/si';
import LoadingLogin from './loaders/LoadingLogin';

const NovaAuth = () => {
  const [form, setForm] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const navigate = useNavigate();
  const authContext = useAuth();

  // Styles
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#0F172A',
      backgroundImage: 'radial-gradient(circle at 10% 20%, rgba(37, 99, 235, 0.1) 0%, transparent 20%), radial-gradient(circle at 90% 80%, rgba(37, 99, 235, 0.1) 0%, transparent 20%)',
      padding: '20px',
      fontFamily: 'Inter, system-ui, sans-serif',
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '20px',
    },
    logo: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#F8FAFC',
      textShadow: '0 0 10px rgba(56, 189, 248, 0.4)',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    logoIcon: {
      fontSize: '28px',
      color: '#38BDF8',
    },
    card: {
      backgroundColor: '#1E293B',
      borderRadius: '16px',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.25)',
      width: '100%',
      maxWidth: '400px',
      overflow: 'hidden',
      border: '1px solid #334155',
    },
    cardHeader: {
      padding: '24px',
      borderBottom: '1px solid #334155',
      textAlign: 'center',
    },
    title: {
      fontSize: '22px',
      fontWeight: '700',
      color: '#F8FAFC',
      marginBottom: '8px',
    },
    subtitle: {
      fontSize: '14px',
      color: '#94A3B8',
    },
    cryptoIcons: {
      display: 'flex',
      justifyContent: 'center',
      gap: '16px',
      marginTop: '16px',
    },
    cryptoIcon: {
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '18px',
    },
    bitcoinIcon: {
      backgroundColor: 'rgba(247, 147, 26, 0.1)',
      color: '#F7931A',
    },
    ethereumIcon: {
      backgroundColor: 'rgba(98, 126, 234, 0.1)',
      color: '#627EEA',
    },
    rippleIcon: {
      backgroundColor: 'rgba(0, 102, 177, 0.1)',
      color: '#0066B1',
    },
    litecoinIcon: {
      backgroundColor: 'rgba(181, 181, 181, 0.1)',
      color: '#B5B5B5',
    },
    form: {
      padding: '24px',
    },
    inputGroup: {
      marginBottom: '20px',
      position: 'relative',
    },
    inputLabel: {
      position: 'absolute',
      top: '50%',
      left: '16px',
      transform: 'translateY(-50%)',
      color: '#64748B',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      pointerEvents: 'none',
    },
    input: {
      width: '100%',
      backgroundColor: '#0F172A',
      borderRadius: '8px',
      border: '1px solid #334155',
      padding: '14px 16px 14px 42px',
      fontSize: '16px',
      color: '#F8FAFC',
      transition: 'border-color 0.2s ease',
    },
    inputFocus: {
      borderColor: '#38BDF8',
      outline: 'none',
      boxShadow: '0 0 0 2px rgba(56, 189, 248, 0.2)',
    },
    error: {
      color: '#EF4444',
      fontSize: '12px',
      marginTop: '6px',
      paddingLeft: '2px',
    },
    button: {
      width: '100%',
      backgroundColor: '#2563EB',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '14px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'background-color 0.2s ease',
      position: 'relative',
      overflow: 'hidden',
    },
    buttonHover: {
      backgroundColor: '#1D4ED8',
    },
    buttonShine: {
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
      animation: 'shine 1.5s infinite',
    },
    message: {
      textAlign: 'center',
      padding: '10px',
      borderRadius: '8px',
      marginBottom: '20px',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    successMessage: {
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      color: '#10B981',
      border: '1px solid rgba(16, 185, 129, 0.2)',
    },
    errorMessage: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#EF4444',
      border: '1px solid rgba(239, 68, 68, 0.2)',
    },
    signupLink: {
      textAlign: 'center',
      marginTop: '24px',
      fontSize: '14px',
      color: '#94A3B8',
    },
    link: {
      color: '#38BDF8',
      textDecoration: 'none',
      fontWeight: '500',
    },
    loaderContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '16px',
    },
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const validateLogin = () => {
    const errors = {};
    if (!form.email) {
      errors.email = 'Email Address is required';
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      errors.email = 'Email Address is invalid';
    }
    if (!form.password) {
      errors.password = 'Password is required';
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateLogin();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setMessage('');
    setMessageType('');

    try {
      // Login logic with user status check
      const result = await authContext.login(form.email, form.password);
      if (result.success) {
        const userStatus = await checkUserStatus();
        
        switch(userStatus) {
          case 'ACTIVE':
          case 'INACTIVE':
            setMessage('Login successful!');
            setMessageType('success');
            setTimeout(() => {
              navigate('/portfolio');
            }, 2000);
            break;
          case 'SUSPENDED':
            setMessage('Your account has been suspended. Please contact the administrator.');
            setMessageType('error');
            authContext.logout();
            break;
          default:
            setMessage('Invalid account status. Please contact support.');
            setMessageType('error');
            authContext.logout();
        }
      } else {
        setMessage(result.message);
        setMessageType('error');
      }
    } catch (error) {
      setMessage('An unexpected error occurred');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.logoContainer}>
        <h1 style={styles.logo}>
          <FaBitcoin style={styles.logoIcon} />
          CryptoNova
        </h1>
      </div>
      
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.title}>Welcome Back</h2>
          <p style={styles.subtitle}>Login to access your crypto dashboard</p>
          <div style={styles.cryptoIcons}>
            <div style={{...styles.cryptoIcon, ...styles.bitcoinIcon}}>
              <FaBitcoin />
            </div>
            <div style={{...styles.cryptoIcon, ...styles.ethereumIcon}}>
              <FaEthereum />
            </div>
            <div style={{...styles.cryptoIcon, ...styles.rippleIcon}}>
              <SiRipple />
            </div>
            <div style={{...styles.cryptoIcon, ...styles.litecoinIcon}}>
              <SiLitecoin />
            </div>
          </div>
        </div>
        
        {message && (
          <div style={{
            ...styles.message,
            ...(messageType === 'success' ? styles.successMessage : styles.errorMessage)
          }}>
            {message}
          </div>
        )}
        
        {loading && (
          <div style={styles.loaderContainer}>
            <LoadingLogin />
          </div>
        )}
        
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label htmlFor="email" style={styles.inputLabel}>
              <FaEnvelope />
            </label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Email Address"
              value={form.email}
              onChange={handleChange}
              style={styles.input}
              onFocus={(e) => e.target.style.borderColor = '#38BDF8'}
              onBlur={(e) => e.target.style.borderColor = '#334155'}
            />
            {errors.email && <p style={styles.error}>{errors.email}</p>}
          </div>
          
          <div style={styles.inputGroup}>
            <label htmlFor="password" style={styles.inputLabel}>
              <FaLock />
            </label>
            <input
              type="password"
              id="password"
              name="password"
              placeholder="Password"
              value={form.password}
              onChange={handleChange}
              style={styles.input}
              onFocus={(e) => e.target.style.borderColor = '#38BDF8'}
              onBlur={(e) => e.target.style.borderColor = '#334155'}
            />
            {errors.password && <p style={styles.error}>{errors.password}</p>}
          </div>
          
          <button 
            type="submit" 
            style={styles.button}
            onMouseOver={(e) => e.target.style.backgroundColor = '#1D4ED8'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#2563EB'}
            disabled={loading}
          >
            <span>Login to Dashboard</span>
            <FaArrowRight />
            <span style={styles.buttonShine}></span>
          </button>
        </form>
        
        <div style={styles.signupLink}>
          Don't have an account? <a href="/signup" style={styles.link}>Sign Up</a>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes shine {
          0% { left: -100%; }
          20% { left: 100%; }
          100% { left: 100%; }
        }
      `}</style>
    </div>
  );
};

export default NovaAuth;